import { parseDescription } from "./TemplateRequestHistory.support";
import { STATUS_CONFIG } from "./statusConfig";
import { Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import * as dates from "common/dates";
import { toSentenceCase } from "common/strings";

// The backend appends every status message into the single `description`
// string, so the only way to give the reader the parts back is to parse it
// and put each part on its own card. See `parseDescription`.

// A status the backend wrote but the frontend has no entry for still gets a
// card; it just renders neutral rather than in a palette colour.
const accentFor = (theme, status) => {
  const color = STATUS_CONFIG[status]?.color;
  const main = color ? theme.palette[color]?.main : undefined;
  return typeof main === "string" ? main : null;
};

// `None` is what the backend writes when a message is added without changing
// the status, so there is no state to name in the title.
const entryTitle = (status) => {
  if (!status || status === "None") {
    return "Updated";
  }
  const known = STATUS_CONFIG[status];
  return `Updated to ${known ? known.label : toSentenceCase(status)}`;
};

// The address itself, not a name guessed from it: the backend holds no name,
// and inferring one from the local part is wrong as often as it is right.
const attributionFor = (author, timestamp) =>
  [author || null, timestamp ? dates.asOfDate(timestamp) : null]
    .filter(Boolean)
    .join(" · ");

const DescriptionCard = ({
  title,
  titleColor,
  accent,
  author,
  timestamp,
  children,
}) => {
  const attribution = attributionFor(author, timestamp);

  return (
    <Paper
      variant={"outlined"}
      sx={{
        p: 1.5,
        borderColor: accent || "divider",
        background: accent
          ? `linear-gradient(to bottom, ${alpha(accent, 0.12)}, transparent)`
          : undefined,
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"baseline"}
        spacing={1}
      >
        <Typography
          variant={"overline"}
          sx={{ color: titleColor, lineHeight: 1.6 }}
        >
          {title}
        </Typography>
        {attribution && (
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            sx={{ textAlign: "right" }}
          >
            {attribution}
          </Typography>
        )}
      </Stack>
      <Typography variant={"body2"} sx={{ whiteSpace: "pre-wrap" }}>
        {children}
      </Typography>
    </Paper>
  );
};

const RequestDescription = ({ request }) => {
  const theme = useTheme();
  const { original, entries } = parseDescription(request?.description);

  if (!original && entries.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      {/* No attribution on the original: both sites that render this already
          name the submitter and the submission time beside it — the card's
          footer here, the sidebar's metadata block in the preview dialog — and
          only the later entries name anyone the reader has not already seen. */}
      {original && (
        <DescriptionCard title={"Description"} titleColor={"text.primary"}>
          {original}
        </DescriptionCard>
      )}
      {entries.map((entry, index) => {
        const accent = accentFor(theme, entry.status);
        return (
          <DescriptionCard
            key={`${entry.timestamp}-${index}`}
            title={entryTitle(entry.status)}
            titleColor={accent || "text.secondary"}
            accent={accent}
            author={entry.author}
            timestamp={entry.timestamp}
          >
            {entry.message}
          </DescriptionCard>
        );
      })}
    </Stack>
  );
};

export default RequestDescription;
